import apiClient from "@/api/client";
import type {
  ActiveLeadershipMap,
  HandOverForm,
  HandOverInput,
  LedJournalResult,
  LedParcelsPayload,
  LedStatus,
  LedSummary,
  MyLedProject,
  PriceListItem,
} from "./types";

const BASE = "/contractor-led";

// ── internal ───────────────────────────────────────────────────────────────────
export const getLedStatus = async (connectionId: string): Promise<LedStatus> => {
  const r = await apiClient.get(`${BASE}/connections/${connectionId}/status/`);
  return r.data.data;
};

export const getHandOverForm = async (connectionId: string): Promise<HandOverForm> => {
  const r = await apiClient.get(`${BASE}/connections/${connectionId}/hand-over-form/`);
  return r.data.data;
};

/** Cennik umowy ramowej firmy — ten sam endpoint, z którego korzysta formularz
 *  zapotrzebowania; pozycje wycofane odpadają. */
export const getPriceList = async (contractorId: string): Promise<PriceListItem[]> => {
  const r = await apiClient.get(`/contractors/${contractorId}/agreements/`);
  const agreements: { line_item_types?: (PriceListItem & { is_active?: boolean })[] }[] = r.data.data ?? [];
  return agreements.flatMap((a) =>
    (a.line_item_types ?? [])
      .filter((l) => l.is_active !== false)
      .map((l) => ({ id: l.id, name: l.name, unit: l.unit, base_rate: l.base_rate })),
  );
};

export const handOver = async (connectionId: string, body: HandOverInput): Promise<LedSummary> => {
  const r = await apiClient.post(`${BASE}/connections/${connectionId}/hand-over/`, body);
  return r.data.data;
};

export const revokeLeadership = async (leadershipId: string, reason: string) => {
  const r = await apiClient.post(`${BASE}/leaderships/${leadershipId}/revoke/`, { reason });
  return r.data.data as { revoked: boolean; connection_id: string };
};

export const changeCaretaker = async (leadershipId: string, caretaker: string): Promise<LedSummary> => {
  const r = await apiClient.post(`${BASE}/leaderships/${leadershipId}/caretaker/`, { caretaker });
  return r.data.data;
};

export const getActiveLeaderships = async (): Promise<ActiveLeadershipMap> => {
  const r = await apiClient.get(`${BASE}/active/`);
  return r.data.data;
};

// ── both sides ─────────────────────────────────────────────────────────────────
export interface LedSummaryResult {
  summary: LedSummary;
  internal: boolean;
}

export const getLedSummary = async (leadershipId: string, preview?: boolean): Promise<LedSummaryResult> => {
  const r = await apiClient.get(`${BASE}/leaderships/${leadershipId}/project/`, asFirm(preview));
  return { summary: r.data.data, internal: !!r.data.meta?.internal };
};

export const getLedParcels = async (leadershipId: string, preview?: boolean): Promise<LedParcelsPayload> => {
  const r = await apiClient.get(`${BASE}/leaderships/${leadershipId}/parcels/`, asFirm(preview));
  return r.data.data;
};

export const getLedJournal = async (leadershipId: string, days: number, preview?: boolean): Promise<LedJournalResult> => {
  const r = await apiClient.get(`${BASE}/leaderships/${leadershipId}/journal/`, {
    params: { days, ...(preview ? { as: "contractor" } : {}) },
  });
  return {
    rows: r.data.data,
    days: r.data.meta?.days ?? days,
    recent: r.data.meta?.recent ?? 0,
    internal: !!r.data.meta?.internal,
  };
};

// ── contractor ─────────────────────────────────────────────────────────────────
export const getMyLedProjects = async (): Promise<MyLedProject[]> => {
  const r = await apiClient.get(`${BASE}/my/projects/`);
  return r.data.data;
};

export const getMyLedProjectByOrder = async (
  orderId: string,
): Promise<{ leadership_id: string; connection_id: string; name: string }> => {
  const r = await apiClient.get(`${BASE}/my/projects/by-order/${orderId}/`);
  return r.data.data;
};

// ── etapy 2–4: sekcje robocze ──────────────────────────────────────────────────
import type {
  AnalysisContractor,
  AnalysisResult,
  InstitutionInput,
  LedEntry,
  LedInstitution,
  LedParcelCard,
  MemoRow,
  OrdersPicker,
  PersonInput,
  RegisterInput,
  RegisterRow,
  ReportsPayload,
  RouteTutorial,
  RouteValidation,
  TilesPayload,
  TitleOptions,
  TitleReportRow,
} from "./types";

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;
const L = (lid: string) => `${BASE}/leaderships/${lid}`;
const data = async <T,>(p: Promise<{ data: { data: T } }>): Promise<T> => (await p).data.data;

/** „Podgląd jak podwykonawca" — backend zawęża odpowiedź do tego, co widzi firma.
 *  Dokładamy tylko przy odczycie; zapisy zawsze idą z naszymi uprawnieniami. */
const asFirm = (preview?: boolean) => (preview ? { params: { as: "contractor" } } : undefined);

function form(fields: Record<string, unknown>, files?: File[], fileField = "files"): FormData {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach((x) => fd.append(k, String(x)));
    else fd.append(k, String(v));
  }
  (files ?? []).forEach((f) => fd.append(fileField, f));
  return fd;
}

// działki i strony
export const getParcelCard = (lid: string, parcelId: string, preview?: boolean) => data<LedParcelCard>(apiClient.get(`${L(lid)}/parcels/${parcelId}/`, asFirm(preview)));
export const setParcelKw = (lid: string, parcelId: string, kw_number: string) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/parcels/${parcelId}/kw/`, { kw_number }));
export const setParcelRoad = (lid: string, parcelId: string, body: { is_internal_road: boolean; note?: string }) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/parcels/${parcelId}/road/`, body));
export const addPerson = (lid: string, parcelId: string, body: PersonInput) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/parcels/${parcelId}/owners/`, body));
export const attachInstitution = (lid: string, parcelId: string, body: { institution: string; role?: string }) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/parcels/${parcelId}/institution/`, body));
export const updateOwner = (lid: string, ownerId: string, body: Partial<PersonInput>) =>
  data<LedParcelCard>(apiClient.patch(`${L(lid)}/owners/${ownerId}/`, body));
export const maritalJoin = (lid: string, ownerId: string, spouse: string) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/owners/${ownerId}/marital/join/`, { spouse }));
export const maritalLeave = (lid: string, ownerId: string) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/owners/${ownerId}/marital/leave/`, {}));

// tytuły
export const getTitleOptions = (lid: string) => data<TitleOptions>(apiClient.get(`${L(lid)}/title-options/`));
export const getTitleReports = (lid: string, preview?: boolean) => data<TitleReportRow[]>(apiClient.get(`${L(lid)}/title-reports/`, asFirm(preview)));
/** GK: tytuł pozyskany dla starej wersji trasy → ostemplowany bieżącą (zdejmuje też „do weryfikacji"). */
export const extendOwnerTitle = (lid: string, ownerId: string, note = "") =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/owners/${ownerId}/title/extend-route/`, { note }));
/** GK: „Zmiana trasy zweryfikowana" — zdejmuje flagę z działki bez ruszania tytułów. */
export const clearRouteReview = (lid: string, parcelId: string, note = "") =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/parcels/${parcelId}/route-review/clear/`, { note }));
export const setTitleStatus = (lid: string, ownerId: string, body: { status: string; note?: string }) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/owners/${ownerId}/title/status/`, body));
export const reportTitle = (
  lid: string,
  ownerId: string,
  body: { basis_code: string; z7_consent_form?: string; document_number?: string; note?: string; files?: File[] },
) => {
  const { files, ...fields } = body;
  return data<LedParcelCard>(apiClient.post(`${L(lid)}/owners/${ownerId}/title/report/`, form(fields, files), MULTIPART));
};
/** Usuwa dokument z tytułu strony; ostatni dokument cofa pozyskanie (backend pisze to do historii). */
export const removeOwnerTitleFile = (lid: string, ownerId: string, fileId: string) =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/owners/${ownerId}/title/files/${fileId}/remove/`));
export const decideTitleReport = (lid: string, reportId: string, decision: "confirm" | "reject", note = "") =>
  data<LedParcelCard>(apiClient.post(`${L(lid)}/title-reports/${reportId}/${decision}/`, { note }));

// instytucje
export const getInstitutions = (lid: string, preview?: boolean) => data<LedInstitution[]>(apiClient.get(`${L(lid)}/institutions/`, asFirm(preview)));
export const createInstitution = (lid: string, body: InstitutionInput) =>
  data<LedInstitution[]>(apiClient.post(`${L(lid)}/institutions/`, body));
export const updateInstitution = (lid: string, id: string, body: Partial<InstitutionInput & { is_active: boolean }>) =>
  data<LedInstitution[]>(apiClient.patch(`${L(lid)}/institutions/${id}/`, body));

// dziennik działań
export const addEntry = (
  lid: string,
  body: { kind: string; occurred_on: string; body?: string; parcel?: string | null; owner?: string | null; milestone?: string | null; files?: File[] },
) => {
  const { files, ...fields } = body;
  return data<LedEntry>(apiClient.post(`${L(lid)}/entries/`, form(fields, files), MULTIPART));
};
export const editEntry = (lid: string, id: string, body: { body?: string; occurred_on?: string }) =>
  data<LedEntry>(apiClient.patch(`${L(lid)}/entries/${id}/`, body));
export const withdrawEntry = (lid: string, id: string, reason: string) =>
  data<LedEntry>(apiClient.post(`${L(lid)}/entries/${id}/withdraw/`, { reason }));

// rejestr spraw
export const getRegister = (lid: string, preview?: boolean) => data<RegisterRow[]>(apiClient.get(`${L(lid)}/register/`, asFirm(preview)));
export const addRegister = (lid: string, body: RegisterInput) => {
  const { files, ...fields } = body;
  return data<RegisterRow>(apiClient.post(`${L(lid)}/register/`, form(fields, files), MULTIPART));
};
export const registerAction = (lid: string, id: string, action: "read" | "share", body: Record<string, unknown> = {}) =>
  data<RegisterRow>(apiClient.post(`${L(lid)}/register/${id}/${action}/`, body));
// kafle
export const getTiles = (lid: string, preview?: boolean) => data<TilesPayload>(apiClient.get(`${L(lid)}/tiles/`, asFirm(preview)));
export const addTile = (lid: string, body: { label: string; due_date?: string | null; side?: string | null }) =>
  data<TilesPayload>(apiClient.post(`${L(lid)}/tiles/`, body));
export const patchTile = (lid: string, milestoneId: string, body: { label?: string; due_date?: string | null; side?: string | null; delete?: boolean }) =>
  data<TilesPayload>(apiClient.patch(`${L(lid)}/tiles/${milestoneId}/`, body));
export const setTileState = (lid: string, milestoneId: string, body: { state: string; reason?: string; note?: string }) =>
  data<TilesPayload>(apiClient.post(`${L(lid)}/tiles/${milestoneId}/state/`, body));
export const uploadTileFiles = (lid: string, milestoneId: string, files: File[]) =>
  data<TilesPayload>(apiClient.post(`${L(lid)}/tiles/${milestoneId}/files/`, form({}, files), MULTIPART));
export const removeTileFile = (lid: string, tileFileId: number) =>
  data<TilesPayload>(apiClient.post(`${L(lid)}/tile-files/${tileFileId}/remove/`, {}));
export const assignTile = (lid: string, milestoneId: string, body: { order?: string | null; contractor?: string | null; internal_note?: string }) =>
  data<TilesPayload>(apiClient.post(`${L(lid)}/tiles/${milestoneId}/assign/`, body));
export const getOrdersPicker = (lid: string) => data<OrdersPicker>(apiClient.get(`${L(lid)}/tiles/orders-picker/`));
export const requestTile = (lid: string, body: { label: string; reason?: string }) =>
  data<TilesPayload>(apiClient.post(`${L(lid)}/tile-requests/`, body));
export const decideTileRequest = (lid: string, reqId: string, body: { accept: boolean; side?: string | null; note?: string; due_date?: string | null }) =>
  data<TilesPayload>(apiClient.post(`${L(lid)}/tile-requests/${reqId}/decide/`, body));

// notatki służbowe
export const getMemos = (lid: string, preview?: boolean) => data<MemoRow[]>(apiClient.get(`${L(lid)}/memos/`, asFirm(preview)));
export const createMemo = (lid: string, body: { subject: string; body: string; parcels?: string[]; milestone?: string | null; files?: File[] }) => {
  const { files, ...fields } = body;
  return data<MemoRow>(apiClient.post(`${L(lid)}/memos/`, form(fields, files), MULTIPART));
};
export const editMemo = (lid: string, id: string, body: { subject?: string; body?: string }) =>
  data<MemoRow>(apiClient.patch(`${L(lid)}/memos/${id}/`, body));
export const readMemo = (lid: string, id: string) => data<MemoRow>(apiClient.post(`${L(lid)}/memos/${id}/read/`, {}));

// raporty
export const getReports = (lid: string, preview?: boolean) => data<ReportsPayload>(apiClient.get(`${L(lid)}/reports/`, asFirm(preview)));
export const createReportCycle = (
  lid: string,
  body: { recipient: string; frequency: string; starts_on: string; weekday?: number; day_of_month?: number; label?: string },
) => data<ReportsPayload>(apiClient.post(`${L(lid)}/report-cycles/`, body));
export const patchReportCycle = (lid: string, id: string, body: { is_active?: boolean; label?: string; weekday?: number; day_of_month?: number; starts_on?: string }) =>
  data<ReportsPayload>(apiClient.patch(`${L(lid)}/report-cycles/${id}/`, body));
export const submitReport = (lid: string, itemId: string, body: { files: File[]; note?: string }) =>
  data<ReportsPayload>(apiClient.post(`${L(lid)}/report-items/${itemId}/submit/`, form({ note: body.note ?? "" }, body.files), MULTIPART));
export const forwardReport = (lid: string, itemId: string) => data<ReportsPayload>(apiClient.post(`${L(lid)}/report-items/${itemId}/forward/`, {}));

// trasa
export const getRouteTutorial = (lid: string, preset?: string | null) =>
  data<RouteTutorial>(apiClient.get(`${L(lid)}/route/tutorial/`, { params: preset ? { preset } : {} }));
export const validateRoute = (lid: string, dxf: File, preset?: string | null) =>
  data<RouteValidation>(apiClient.post(`${L(lid)}/route/validate/`, form({}, [dxf], "dxf_file"), { ...MULTIPART, params: preset ? { preset } : {} }));
export const uploadRoute = (lid: string, dxf: File, opts: { preset?: string | null; comment?: string }) =>
  data<{ task_id: string }>(
    apiClient.post(`${L(lid)}/route/upload/`, form({ layer_preset: opts.preset ?? undefined, comment: opts.comment ?? "" }, [dxf], "dxf_file"), MULTIPART),
  );

/** Pobranie pliku przez apiClient (nagłówek JWT) i zapis w przeglądarce. */
async function downloadBlob(url: string, params: Record<string, string>, filename: string) {
  const r = await apiClient.get(url, { params, responseType: "blob" });
  const href = URL.createObjectURL(r.data as Blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 5_000);
}
export const downloadRouteTemplate = (lid: string, preset: string, presetName: string) =>
  downloadBlob(`${L(lid)}/route/template/`, { preset }, `szablon_${presetName || "trasa"}.dxf`);
export const downloadJournalCsv = (lid: string, days: number, shortName: string) =>
  downloadBlob(`${L(lid)}/journal/export.csv`, { days: String(days) }, `dziennik_${shortName || "projekt"}.csv`);

// analiza postępów podwykonawcy (dyr. tech. / CEO)
export const getLeadershipAnalysis = (lid: string, days: number) =>
  data<AnalysisResult>(apiClient.get(`${L(lid)}/analysis/`, { params: { days } }));
export const getAnalysisContractors = () => data<AnalysisContractor[]>(apiClient.get(`${BASE}/analysis/contractors/`));
export const getAnalysis = (contractor: string, days: number) =>
  data<AnalysisResult>(apiClient.get(`${BASE}/analysis/`, { params: { contractor, days } }));

// ── zakładka „Pliki" (views_files.py) — każda mutacja odpowiada świeżym drzewem ────
import { saveBlobResponse } from "@/utils/download";
import type { LedFilesPayload } from "./types";

const F = (lid: string) => `${L(lid)}/files`;
const filesPayload = async (p: Promise<{ data: { data: LedFilesPayload; meta?: { internal?: boolean } } }>): Promise<LedFilesPayload> => {
  const r = await p;
  // `internal` w meta jest źródłem prawdy (podgląd „jak podwykonawca" zmienia go na false).
  return { ...r.data.data, internal: !!r.data.meta?.internal };
};

export const getLedFiles = (lid: string, preview?: boolean) => filesPayload(apiClient.get(`${F(lid)}/`, asFirm(preview)));
/** Jeden plik na żądanie — opis jest per plik, jak w zakładce „Pliki" DLC_01. */
export const uploadLedFile = (lid: string, file: File, folderSlug: string, description: string) =>
  filesPayload(apiClient.post(`${F(lid)}/upload/`, form({ folder_slug: folderSlug, description }, [file]), MULTIPART));
export const patchLedFile = (lid: string, fileId: string, body: { description?: string; filename?: string }) =>
  filesPayload(apiClient.patch(`${F(lid)}/${fileId}/`, body));
export const removeLedFile = (lid: string, fileId: string) => filesPayload(apiClient.post(`${F(lid)}/${fileId}/remove/`, {}));
export const setLedFileHidden = (lid: string, fileId: string, hidden: boolean) =>
  filesPayload(apiClient.post(`${F(lid)}/${fileId}/hide/`, { hidden }));
export const createLedFolder = (lid: string, label: string) => filesPayload(apiClient.post(`${F(lid)}/folders/`, { label }));
export const renameLedFolder = (lid: string, slug: string, label: string) =>
  filesPayload(apiClient.post(`${F(lid)}/folders/rename/`, { slug, label }));
export const deleteLedFolder = (lid: string, slug: string) => filesPayload(apiClient.post(`${F(lid)}/folders/delete/`, { slug }));
export const setLedFolderHidden = (lid: string, slug: string, hidden: boolean) =>
  filesPayload(apiClient.post(`${F(lid)}/folders/hide/`, { slug, hidden }));
export const transferLedFiles = (
  lid: string,
  body: { operation: "copy" | "move"; target_slug: string; file_id?: string; source_slug?: string },
) => filesPayload(apiClient.post(`${F(lid)}/transfer/`, body));
/** (Ponowna) kopia plików projektu — ELEKTIM; idzie w tle (task_id w odpowiedzi). */
export const snapshotLedFiles = (lid: string) => filesPayload(apiClient.post(`${F(lid)}/snapshot/`, {}));
/** Jeden plik projektu ELEKTIM jeszcze nieskopiowany → zakładka; `hidden` = „ukryj dla podwykonawcy" od razu. */
export const copyPendingLedFile = (lid: string, body: { managed_file_id: string; folder_slug: string; hidden: boolean }) =>
  filesPayload(apiClient.post(`${F(lid)}/pending/copy/`, body));
export const downloadLedFilesZip = async (lid: string, folderSlug?: string, preview?: boolean) => {
  const resp = await apiClient.get(`${F(lid)}/zip/`, {
    params: { ...(folderSlug ? { folder: folderSlug } : {}), ...(preview ? { as: "contractor" } : {}) },
    responseType: "blob",
  });
  saveBlobResponse(resp, folderSlug ? `Pliki__${folderSlug}.zip` : "Pliki.zip");
};
